import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Modal } from "../../components/ui/modal";
import { CheckCircle } from "lucide-react";
import { styles } from "../../styles/pages/SubCategory/SubCategoryAddStyles";
import ListboxComponent from "../../components/common/ListboxComponent";

interface Category {
  id: number;
  category_name: string;
}

const SubCategoryAdd = () => {
  const navigate = useNavigate();
  const [subcatName, setSubcatName] = useState("");
  const [subcatDesc, setSubcatDesc] = useState("");
  const [proCatId, setProCatId] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{
    subcat_name?: string;
    pro_cat_id?: string;
  }>({});
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  // const [categories, setCategories] = useState<{ id: number; category_name: string }[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  const token = sessionStorage.getItem("access_token");
  const userId = sessionStorage.getItem("user_id");

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        if (!token) throw new Error("Unauthorized: Please log in.");
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/category/name/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.status === 200) {
          setCategories(response.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      }
    };
    fetchCategories();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    const newErrors: { subcat_name?: string; pro_cat_id?: string } = {};

    if (!subcatName.trim()) {
      newErrors.subcat_name = "The Subcategory name field is required";
    }
    if (!proCatId) {
      newErrors.pro_cat_id = "Please select a category";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSaving(false);
      return;
    }

    const formData = new FormData();
    formData.append("subcat_name", subcatName);
    formData.append("subcat_desc", subcatDesc);
    formData.append("user_id", userId || "");
    formData.append("pro_cat_id", proCatId);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/subcategory/add`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === 200) {
        setSuccessModalOpen(true);
        setTimeout(() => {
          setSuccessModalOpen(false);
          navigate("/subcategory-list");
        }, 2000);
        setSubcatName("");
        setSubcatDesc("");
        setProCatId("");
        return;
      }
      if (response.data.status === 422 && response.data.message) {
        setErrors(
          Object.fromEntries(
            Object.entries(response.data.message).map(([key, value]) => [
              key,
              Array.isArray(value) ? value[0] : value,
            ])
          )
        );
        return;
      }
      if (response.data.status === 500) {
        setErrors({
          subcat_name: response.data.message || "Internal Server Error",
        });
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      setErrors({ subcat_name: errorMessage });

      if (error.response?.status === 422 && error.response.data.errors) {
        setErrors(
          Object.fromEntries(
            Object.entries(error.response.data.errors).map(([key, value]) => [
              key,
              Array.isArray(value) ? value[0] : value,
            ])
          )
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === "subcat_name") {
      setSubcatName(value);
      if (!value.trim()) {
        setErrors({ ...errors, subcat_name: "The Subcategory name field is required" });
      } else {
        setErrors({ ...errors, subcat_name: undefined });
      }
    } else if (name === "subcat_desc") {
      setSubcatDesc(value);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Add Sub Category</h2>
      </div>

      <div className={styles.formContainer}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.inputContainer}>
            <div>
              <label className={styles.label}>
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="subcat_name"
                value={subcatName}
                onChange={handleInputChange}
                className={`${styles.input} ${errors.subcat_name
                  ? styles.inputError
                  : "border-gray-300 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10"
                  }`}
              />
              {errors.subcat_name && (
                <span className={styles.errorText}>{errors.subcat_name}</span>
              )}
            </div>
            <ListboxComponent
              value={categories.find((cat) => cat.id === Number(proCatId)) ?? null}
              onChange={(cat) => {
                const value = cat ? String(cat.id) : "";
                setProCatId(value);
                setErrors((prev) => ({
                  ...prev,
                  pro_cat_id: value ? undefined : "Please select a category",
                }));
              }}
              items={categories}
              label="Category"
              displayKey="category_name"
              error={errors.pro_cat_id}
              required
            />

            <div>
              <label className={styles.label}>Description</label>
              <textarea
                name="subcat_desc"
                value={subcatDesc}
                onChange={handleInputChange}
                className={`${styles.textareaField} h-24`}
                style={{ textAlign: "justify" }}
              />
            </div>
          </div>

          <div className={styles.buttonContainer}>
            <button
              type="submit"
              disabled={saving}
              className={styles.submitButton}
            >
              {saving ? "Saving..." : "Submit"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/subcategory-list")}
              className={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </form>

        <Modal
          isOpen={isSuccessModalOpen}
          onClose={() => setSuccessModalOpen(false)}
          className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
        >
          <div className="flex justify-center">
            <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
          <h4 className="text-lg font-bold text-gray-800 dark:text-white mt-4">
            Subcategory Added Successfully
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            The subcategory has been added to the system successfully.
          </p>
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setSuccessModalOpen(false)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              OK
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default SubCategoryAdd;